import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios, { API } from '../services/api';
import { motion } from 'framer-motion';

/*
  Founder Growth Dashboard — /founder/growth
  Mission control for Kindra. 30-second scannability.
*/

const PAPER = '#FBFAF6';
const NAVY = '#0E1C2E';
const NAVY_MUTED = 'rgba(14, 28, 46, 0.7)';
const NAVY_SOFT = 'rgba(14, 28, 46, 0.55)';
const TEAL = '#2FB3A0';
const HAIRLINE = 'rgba(14, 28, 46, 0.10)';

const isFounder = (u) => !!u && u.role === 'admin';

// ─── Tile primitives ────────────────────────────────────
const Eyebrow = ({ children }) => (
  <p
    className="font-sans text-[10px] md:text-[11px] font-medium uppercase"
    style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}
  >
    {children}
  </p>
);

const Big = ({ children, color = NAVY }) => (
  <p
    className="font-serif text-[44px] md:text-[58px] leading-[1.05] tracking-[-0.01em] font-normal"
    style={{ color }}
  >
    {children}
  </p>
);

const Sub = ({ children, color = NAVY_SOFT }) => (
  <p className="font-sans text-[13px] md:text-[14px] mt-2" style={{ color }}>{children}</p>
);

const Tile = ({ eyebrow, value, sub, accent = false, testid }) => (
  <div
    className="relative rounded-[20px] p-6 md:p-7 flex flex-col justify-between min-h-[148px] md:min-h-[170px]"
    style={{
      backgroundColor: accent ? '#FFFFFF' : PAPER,
      border: `1px solid ${HAIRLINE}`,
    }}
    data-testid={testid}
  >
    <Eyebrow>{eyebrow}</Eyebrow>
    <div className="mt-3">
      <Big>{value}</Big>
      {sub && <Sub>{sub}</Sub>}
    </div>
  </div>
);

const formatPct = (n) => (typeof n === 'number' ? `${n}%` : '—');
const formatNum = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');

// ─── Time range tabs ────────────────────────────────────
const RANGES = [
  { d: 1,  label: 'Today' },
  { d: 7,  label: '7 days' },
  { d: 30, label: '30 days' },
  { d: 90, label: '90 days' },
];

// ─── Main page ──────────────────────────────────────────
const FounderGrowthPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!user || !isFounder(user)) return;
    let cancelled = false;
    (async () => {
      try {
        setErr(null);
        const r = await axios.get(`${API}/founder/growth?days=${days}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || 'Failed to load growth data.');
      }
    })();
    return () => { cancelled = true; };
  }, [user, days, refreshKey]);

  const kpis = data?.kpis || {};
  const ns = data?.north_star || {};
  const friction = data?.friction || {};
  const fricCounts = friction.counts || {};
  const fricFeed = friction.feed || [];
  const drop = kpis.biggest_drop_off || {};

  // ─── 7 hero metrics that must be most prominent ────────
  const hero = useMemo(() => ([
    { eyebrow: 'New registrations', value: formatNum(kpis.new_registrations), sub: kpis.homepage_visitors ? `${formatNum(kpis.homepage_visitors)} visited` : null, testid: 'kpi-new-registrations' },
    { eyebrow: 'Onboarding completion', value: formatPct(kpis.onboarding_completion_pct), sub: `${kpis.onboarding_completed || 0} of ${kpis.new_registrations || 0}`, testid: 'kpi-onboarding-completion' },
    { eyebrow: 'First upload', value: formatPct(kpis.first_upload_pct), sub: `${kpis.first_upload || 0} users`, testid: 'kpi-first-upload' },
    { eyebrow: 'First chat', value: formatPct(kpis.first_chat_pct), sub: `${kpis.first_chat || 0} users`, testid: 'kpi-first-chat' },
    { eyebrow: 'Day 1 return', value: formatPct(kpis.d1_return_pct), sub: `${kpis.d1_returned || 0} of ${kpis.d1_eligible || 0} eligible`, testid: 'kpi-d1-return' },
    { eyebrow: 'North Star reached', value: formatNum(ns.total), sub: 'received ongoing value', testid: 'kpi-north-star' },
    { eyebrow: 'Biggest drop-off', value: drop.drop_pct ? `${drop.drop_pct}%` : '—', sub: drop.from && drop.to ? `${drop.from} → ${drop.to}` : null, testid: 'kpi-biggest-dropoff' },
  ]), [kpis, ns, drop]);

  // ─── Secondary tiles ───────────────────────────────────
  const secondary = useMemo(() => ([
    { eyebrow: 'First check-in',         value: formatPct(kpis.first_checkin_pct),    sub: `${kpis.first_checkin || 0} users`,      testid: 'kpi-first-checkin' },
    { eyebrow: 'Day 7 return',           value: formatPct(kpis.d7_return_pct),         sub: `${kpis.d7_returned || 0} of ${kpis.d7_eligible || 0} eligible`, testid: 'kpi-d7-return' },
    { eyebrow: 'Avg uploads / user',     value: kpis.avg_uploads_per_user ?? '—',     sub: 'in window',                              testid: 'kpi-avg-uploads' },
    { eyebrow: 'Avg chats / user',       value: kpis.avg_chats_per_user ?? '—',       sub: 'in window',                              testid: 'kpi-avg-chats' },
    { eyebrow: 'Trial ending ≤ 3 days',  value: formatNum(kpis.trial_users_approaching_30), sub: 'subscribe-or-leave window',         testid: 'kpi-trial-warning' },
    { eyebrow: 'Homepage conversion',    value: formatPct(kpis.homepage_conversion_pct), sub: `${kpis.new_registrations || 0} of ${kpis.homepage_visitors || 0}`, testid: 'kpi-homepage-conversion' },
  ]), [kpis]);

  // ─── Activation Welcome Chat tiles ─────────────────────
  const welcomeTiles = useMemo(() => ([
    { eyebrow: 'Welcome chat opened',      value: formatNum(kpis.welcome_opened),         sub: 'right after onboarding',          testid: 'kpi-welcome-opened' },
    { eyebrow: 'First message sent',       value: formatPct(kpis.welcome_first_msg_pct),  sub: `${kpis.welcome_first_msg_sent || 0} of ${kpis.welcome_opened || 0}`, testid: 'kpi-welcome-first-msg' },
    { eyebrow: 'Upload after welcome',     value: formatPct(kpis.welcome_upload_conv_pct),sub: `${kpis.welcome_upload_clicked || 0} clicked upload prompt`,        testid: 'kpi-welcome-upload-conv' },
    { eyebrow: 'Welcome session (median)', value: kpis.welcome_duration_p50 ? `${kpis.welcome_duration_p50}s` : '—',                  sub: 'P50 time in chat',                testid: 'kpi-welcome-duration' },
  ]), [kpis]);

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: PAPER }}><p style={{ color: NAVY_SOFT }} className="font-sans text-sm">Loading...</p></div>;
  if (!isFounder(user)) {
    navigate('/home', { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: PAPER, color: NAVY }} data-testid="founder-growth-page">
      {/* Header */}
      <header
        className="sticky top-0 z-10 backdrop-blur-md"
        style={{ backgroundColor: 'rgba(251, 250, 246, 0.92)', borderBottom: `1px solid ${HAIRLINE}` }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 md:py-7 flex items-center justify-between gap-4">
          <div>
            <Eyebrow>Founder · Mission Control</Eyebrow>
            <h1 className="font-serif text-[28px] md:text-[36px] leading-[1.1] tracking-[-0.01em] font-normal mt-1">
              Growth & Proof
            </h1>
          </div>
          <div className="flex items-center gap-1 rounded-full border" style={{ borderColor: HAIRLINE, backgroundColor: '#fff' }} data-testid="growth-range-tabs">
            {RANGES.map(r => (
              <button
                key={r.d}
                onClick={() => setDays(r.d)}
                className={`px-3.5 md:px-4 py-2 text-[12px] md:text-[13px] font-sans font-medium rounded-full transition-colors ${days === r.d ? 'text-white' : 'hover:bg-slate-50'}`}
                style={days === r.d ? { backgroundColor: NAVY } : { color: NAVY_MUTED }}
                data-testid={`growth-range-${r.d}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 pb-32 pt-10 md:pt-12">
        {err && (
          <div className="rounded-[16px] p-5 mb-8 font-sans text-[14px]" style={{ backgroundColor: '#FEE2E2', color: '#7F1D1D' }} data-testid="growth-error">
            {err}
          </div>
        )}

        {/* Today's biggest problem — single insight sentence */}
        <motion.section
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="rounded-[24px] p-8 md:p-10 mb-12"
          style={{ backgroundColor: NAVY, color: PAPER }}
          data-testid="growth-insight"
        >
          <p className="font-sans text-[10px] md:text-[11px] font-medium uppercase mb-3" style={{ letterSpacing: '0.22em', color: 'rgba(251,250,246,0.55)' }}>
            Today’s biggest problem
          </p>
          <p className="font-serif italic text-[26px] md:text-[36px] lg:text-[42px] leading-[1.18] tracking-[-0.01em] font-normal" data-testid="growth-insight-text">
            “{data?.insight || 'Loading insight…'}”
          </p>
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            className="mt-6 inline-flex items-center gap-2 font-sans text-[12px] font-medium uppercase rounded-full px-4 py-2 border"
            style={{ borderColor: 'rgba(251,250,246,0.25)', color: 'rgba(251,250,246,0.85)', letterSpacing: '0.18em' }}
            data-testid="growth-refresh"
          >
            Refresh
          </button>
        </motion.section>

        {/* Hero KPI strip — 7 most important */}
        <section className="mb-12 md:mb-16" data-testid="growth-hero-kpis">
          <Eyebrow>Hero metrics</Eyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5 mt-4">
            {hero.map((t, i) => (
              <motion.div
                key={t.testid}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.04 * i }}
              >
                <Tile {...t} accent />
              </motion.div>
            ))}
          </div>
        </section>

        {/* Secondary KPIs */}
        <section className="mb-12 md:mb-16" data-testid="growth-secondary-kpis">
          <Eyebrow>Secondary metrics</Eyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-4">
            {secondary.map((t, i) => (
              <motion.div
                key={t.testid}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.03 * i }}
              >
                <Tile {...t} />
              </motion.div>
            ))}
          </div>
        </section>

        {/* Activation Welcome Chat — the new post-onboarding loop */}
        <section className="mb-12 md:mb-16" data-testid="growth-welcome-kpis">
          <Eyebrow>Activation · Welcome chat</Eyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-4">
            {welcomeTiles.map((t, i) => (
              <motion.div
                key={t.testid}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.03 * i }}
              >
                <Tile {...t} accent />
              </motion.div>
            ))}
          </div>
        </section>

        {/* North Star breakdown */}
        <section className="mb-12 md:mb-16" data-testid="growth-northstar-breakdown">
          <Eyebrow>North Star · breakdown</Eyebrow>
          <div className="rounded-[24px] p-8 md:p-10 mt-4" style={{ backgroundColor: '#fff', border: `1px solid ${HAIRLINE}` }}>
            <p className="font-serif text-[28px] md:text-[36px] leading-[1.15] tracking-[-0.01em] font-normal max-w-[820px]">
              <span style={{ color: TEAL }}>{ns.total ?? 0}</span> users received ongoing value{' '}
              <span style={{ color: NAVY_SOFT }} className="text-[18px] md:text-[24px]">in this window.</span>
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
              {[
                ['Uploaded a doc', ns.breakdown?.uploaded_doc, 'ns-uploaded'],
                ['3+ check-ins', ns.breakdown?.checkins_3plus, 'ns-checkins'],
                ['2+ visit days', ns.breakdown?.returned_2days, 'ns-returned'],
                ['Used appt prep', ns.breakdown?.appt_prep_used, 'ns-prep'],
                ['3+ chats', ns.breakdown?.chats_3plus, 'ns-chats'],
                ['Opened digest', ns.breakdown?.digest_opened, 'ns-digest'],
              ].map(([label, count, tid]) => (
                <div key={label} className="flex flex-col py-3 border-l-2 pl-4" style={{ borderLeftColor: HAIRLINE }} data-testid={tid}>
                  <p className="font-serif text-[24px] md:text-[28px] leading-none tracking-[-0.01em]">{count ?? 0}</p>
                  <p className="font-sans text-[12px] mt-1.5 uppercase" style={{ color: NAVY_SOFT, letterSpacing: '0.18em' }}>{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Friction events */}
        <section data-testid="growth-friction">
          <Eyebrow>Friction · what users hit</Eyebrow>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6 mt-4">
            {/* Counts */}
            <div className="rounded-[24px] p-7 md:p-8 lg:col-span-1" style={{ backgroundColor: '#fff', border: `1px solid ${HAIRLINE}` }} data-testid="friction-counts">
              <p className="font-sans text-[10px] md:text-[11px] font-medium uppercase mb-5" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>Counts</p>
              <div className="space-y-4">
                {[
                  ['Rage clicks',           'rage_click',          'fric-rage_click'],
                  ['Onboarding abandoned',  'onboarding_abandoned','fric-onboarding'],
                  ['Upload abandoned',      'upload_abandoned',    'fric-upload'],
                  ['Chat exit no send',     'chat_exit_no_send',   'fric-chat'],
                  ['Nav loops',             'nav_loop',            'fric-nav'],
                ].map(([label, key, tid]) => (
                  <div key={key} className="flex items-baseline justify-between" data-testid={tid}>
                    <span className="font-sans text-[14px]" style={{ color: NAVY_MUTED }}>{label}</span>
                    <span className="font-serif text-[24px] leading-none" style={{ color: (fricCounts[key] || 0) > 0 ? '#B91C1C' : NAVY }}>{fricCounts[key] || 0}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Feed */}
            <div className="rounded-[24px] p-7 md:p-8 lg:col-span-2" style={{ backgroundColor: '#fff', border: `1px solid ${HAIRLINE}` }} data-testid="friction-feed">
              <div className="flex items-baseline justify-between mb-5">
                <p className="font-sans text-[10px] md:text-[11px] font-medium uppercase" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>Recent friction</p>
                <p className="font-sans text-[12px]" style={{ color: NAVY_SOFT }}>{fricFeed.length} events</p>
              </div>
              {fricFeed.length === 0 ? (
                <p className="font-sans text-[14px] py-8 text-center" style={{ color: NAVY_SOFT }} data-testid="friction-feed-empty">
                  No friction events yet — clean.
                </p>
              ) : (
                <ul className="divide-y" style={{ borderColor: HAIRLINE }}>
                  {fricFeed.map((f, i) => (
                    <li key={i} className="py-3 flex items-center justify-between gap-4" data-testid={`friction-event-${i}`}>
                      <div className="min-w-0">
                        <p className="font-sans text-[13px] font-medium" style={{ color: NAVY }}>{f.event.replace(/_/g, ' ')}</p>
                        <p className="font-sans text-[12px] truncate" style={{ color: NAVY_SOFT }}>{f.user_email}</p>
                      </div>
                      <p className="font-sans text-[11px] uppercase tracking-wider flex-shrink-0" style={{ color: NAVY_SOFT, letterSpacing: '0.16em' }}>
                        {f.ago}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        <p className="font-sans text-[11px] uppercase mt-16 text-center" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>
          Window: last {days} days · {data?.since?.slice(0,10) || ''} → today
        </p>
      </main>
    </div>
  );
};

export default FounderGrowthPage;
